/**
 * Detects client-side network failures — the browser could not complete the
 * request (offline, flaky connection, DNS, blocked by an extension, CORS
 * preflight failure, a provider SDK's own fetch failing). These are not backend
 * faults and are not actionable for us, so callers log them below `error` (or
 * skip reporting) to keep them off the `status:error` monitors.
 *
 * Matches only the exact, case-sensitive messages browsers emit for a failed
 * fetch — Chrome/Edge "Failed to fetch", Safari "Load failed", Firefox
 * "NetworkError" — mirroring the toast layer's `isNetworkError`
 * (packages/ui/.../userFacingErrors.ts). The case sensitivity matters: a
 * lowercase substring match would also catch genuine app errors like
 * "Upload failed" or "Failed to load chunk". A wrapped SDK error (e.g. ClerkJS
 * "Network error … TypeError: Failed to fetch") still matches via "Failed to
 * fetch". Plus an explicit offline signal.
 */
const CLIENT_NETWORK_ERROR_FRAGMENTS = [
  "Failed to fetch",
  "Load failed",
  "NetworkError",
]

export function isClientNetworkError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return true
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : ""
  if (!message) {
    return false
  }

  return CLIENT_NETWORK_ERROR_FRAGMENTS.some((fragment) =>
    message.includes(fragment),
  )
}
